import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { useDebouncedValue } from 'hooks';
import { useLocalStorage } from 'services/hooks';
import { displayErrorMessage } from 'utilities/displayErrorMessage';

const SELECTED_PREPARER_IDS_KEY = 'modelsWorkboardSelectedPreparerIds';
const SELECTED_REVIEWER_IDS_KEY = 'modelsWorkboardSelectedReviewerIds';
const SELECTED_PREPARERS_MAP_KEY = 'modelsWorkboardSelectedPreparersMap';
const SELECTED_REVIEWERS_MAP_KEY = 'modelsWorkboardSelectedReviewersMap';

interface UserMap {
  [userId: number]: {
    id: number;
    fullName: string;
  };
}

type UserRole = 'preparer' | 'reviewer';

export interface UseModelFiltersProps {
  onFiltersChange: (filterState: {
    searchParam: string;
    searchStatus: string;
    fromDate: Date | null;
    toDate: Date | null;
    preparerIds: number[];
    reviewerIds: number[];
  }) => void;
}

// Helper function to sort and merge users with selected users
const sortAndMergeUsers = (usersData: CustomUser[] | undefined, selectedUsers: CustomUser[]): CustomUser[] => {
  const users = usersData || [];
  const searchResultIds = new Set(users.map(u => u.id).filter(Boolean));

  const selectedUsersNotInResults = selectedUsers.filter(
    selected => selected.id && !searchResultIds.has(selected.id)
  );

  const allUsers = [...selectedUsersNotInResults, ...users];

  const uniqueUsers = Array.from(
    new Map(allUsers.map(user => [user.id, user])).values()
  );

  return uniqueUsers.sort((a, b) => {
    const aSelected = selectedUsers.some(selected => selected.id === a.id);
    const bSelected = selectedUsers.some(selected => selected.id === b.id);

    if (aSelected && !bSelected) {
      return -1;
    }
    if (!aSelected && bSelected) {
      return 1;
    }
    return 0;
  });
};

// Helper function to restore users from localStorage
const restoreUsersFromStorage = (
  storedIds: number[],
  storedMap: UserMap,
  currentUsers: CustomUser[]
): CustomUser[] => {
  if (
    Array.isArray(storedIds) &&
    storedIds.length > 0 &&
    currentUsers.length === 0 &&
    Object.keys(storedMap).length > 0
  ) {
    return storedIds
      .map(userId => {
        const userData = storedMap[userId];
        if (userData) {
          const nameParts = userData.fullName.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          return {
            id: userData.id,
            first_name: firstName,
            last_name: lastName,
            is_staff: true,
          } as CustomUser;
        }
        return null;
      })
      .filter((user): user is CustomUser => user !== null);
  }
  return [];
};

// Helper function to create user map from selected users
const createUserMap = (selectedUsers: CustomUser[]): UserMap => {
  const usersMap: UserMap = {};
  selectedUsers.forEach(user => {
    if (user.id) {
      const fullName =
        [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || user.username || '';
      usersMap[user.id] = {
        id: user.id,
        fullName,
      };
    }
  });
  return usersMap;
};

export const useModelFilters = ({ onFiltersChange }: UseModelFiltersProps) => {
  const [searchParam, setSearchParam] = useState('');
  const [searchStatus, setSearchStatus] = useState('default');
  const [fromDueDate, setFromDueDate] = useState<Date | null>(null);
  const [toDueDate, setToDueDate] = useState<Date | null>(null);
  const [selectedPreparers, setSelectedPreparers] = useState<CustomUser[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<CustomUser[]>([]);
  const [preparerSearchInput, setPreparerSearchInput] = useState('');
  const [reviewerSearchInput, setReviewerSearchInput] = useState('');

  const [storedPreparerIds, setStoredPreparerIds] = useLocalStorage<number[]>(SELECTED_PREPARER_IDS_KEY, []);
  const [storedReviewerIds, setStoredReviewerIds] = useLocalStorage<number[]>(SELECTED_REVIEWER_IDS_KEY, []);
  const [storedPreparersMap, setStoredPreparersMap] = useLocalStorage<UserMap>(SELECTED_PREPARERS_MAP_KEY, {});
  const [storedReviewersMap, setStoredReviewersMap] = useLocalStorage<UserMap>(SELECTED_REVIEWERS_MAP_KEY, {});

  const debouncedSearchParam = useDebouncedValue(searchParam, 500);
  const debouncedPreparerSearch = useDebouncedValue(preparerSearchInput, 500);
  const debouncedReviewerSearch = useDebouncedValue(reviewerSearchInput, 500);

  // Fetch users for autocomplete (preparers and reviewers use the same API)
  const { data: preparersData, isLoading: isLoadingPreparers } = useQuery({
    queryKey: ['users-list', 'preparer', debouncedPreparerSearch],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(debouncedPreparerSearch || undefined);
      return (response as CustomUser[]) || [];
    },
    enabled: true,
  });

  const { data: reviewersData, isLoading: isLoadingReviewers } = useQuery({
    queryKey: ['users-list', 'reviewer', debouncedReviewerSearch],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(debouncedReviewerSearch || undefined);
      return (response as CustomUser[]) || [];
    },
    enabled: true,
  });

  const sortedPreparers = useMemo(
    () => sortAndMergeUsers(preparersData, selectedPreparers),
    [preparersData, selectedPreparers]
  );

  const sortedReviewers = useMemo(
    () => sortAndMergeUsers(reviewersData, selectedReviewers),
    [reviewersData, selectedReviewers]
  );

  // Restore selected users from stored mapping on mount
  useEffect(() => {
    const restoredPreparers = restoreUsersFromStorage(storedPreparerIds, storedPreparersMap, selectedPreparers);
    if (restoredPreparers.length > 0) {
      setSelectedPreparers(restoredPreparers);
    }

    const restoredReviewers = restoreUsersFromStorage(storedReviewerIds, storedReviewersMap, selectedReviewers);
    if (restoredReviewers.length > 0) {
      setSelectedReviewers(restoredReviewers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync filters to parent and localStorage
  useEffect(() => {
    const preparerIds = selectedPreparers.map(user => user.id).filter((id): id is number => id !== undefined);
    const reviewerIds = selectedReviewers.map(user => user.id).filter((id): id is number => id !== undefined);

    // Update stored IDs and mappings
    setStoredPreparerIds(preparerIds);
    setStoredPreparersMap(createUserMap(selectedPreparers));
    setStoredReviewerIds(reviewerIds);
    setStoredReviewersMap(createUserMap(selectedReviewers));

    // Pass filter state to parent
    onFiltersChange({
      searchParam: debouncedSearchParam,
      searchStatus,
      fromDate: fromDueDate,
      toDate: toDueDate,
      preparerIds,
      reviewerIds,
    });
  }, [
    debouncedSearchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    onFiltersChange,
    setStoredPreparerIds,
    setStoredReviewerIds,
    setStoredPreparersMap,
    setStoredReviewersMap,
  ]);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchStatusChange = (event: { target: { value: unknown } }) => {
    setSearchStatus(event.target.value as string);
  };

  const handleFromDueDateChange = (date: Date | null) => {
    setFromDueDate(date);
  };

  const handleToDueDateChange = (date: Date | null) => {
    if (fromDueDate && date && fromDueDate > date) {
      displayErrorMessage('From Valuation Date must be before To Valuation Date');
      return;
    }
    setToDueDate(date);
  };

  const handleUsersChange = (role: UserRole) => (_event: unknown, newValue: CustomUser[]) => {
    if (role === 'preparer') {
      setSelectedPreparers(newValue);
    } else {
      setSelectedReviewers(newValue);
    }
  };

  const cleanFilters = () => {
    setSearchParam('');
    setSearchStatus('default');
    setFromDueDate(null);
    setToDueDate(null);
    setSelectedPreparers([]);
    setSelectedReviewers([]);
    setPreparerSearchInput('');
    setReviewerSearchInput('');
    setStoredPreparerIds([]);
    setStoredReviewerIds([]);
    setStoredPreparersMap({});
    setStoredReviewersMap({});
  };

  return {
    // State
    searchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    sortedPreparers,
    sortedReviewers,
    isLoadingPreparers,
    isLoadingReviewers,
    // Handlers
    handleSearchParamChange,
    handleSearchStatusChange,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange: handleUsersChange('preparer'),
    handleReviewersChange: handleUsersChange('reviewer'),
    setPreparerSearchInput,
    setReviewerSearchInput,
    cleanFilters,
  };
};

